import { useState } from 'react';
import { Mail, Clock, Check, Phone, Download } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { ActiveOrg } from '@/lib/api';

const SUPPORT_EMAIL = 'esaycall@abaye.co';

const INCLUDED = [
  'שאלונים ללא הגבלה, מחזורים ותפריט שלוחה',
  'ניהול לקוחות וייבוא מאקסל',
  'דשבורד וייצוא לאקסל',
  'ריבוי מנהלים לארגון',
  'גיבויים, עדכונים ותמיכה'
];

export default function Upgrade({ org }: { org: ActiveOrg }) {
  const [downloading, setDownloading] = useState(false);
  const subject = encodeURIComponent(`שדרוג Easy Call - ${org.name}`);
  const body = encodeURIComponent(
    `שלום,\nאני מעוניין/ת לשדרג את הארגון "${org.name}" (מזהה: ${org.slug}) לחשבון מלא.`
  );

  async function downloadRawData() {
    setDownloading(true);
    try {
      const res = await fetch('/api/me/org-backup.zip', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('download_failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `${org.slug}-backup.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success('הנתונים הגולמיים הורדו');
    } catch {
      toast.error('שגיאה בהורדת הנתונים');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 py-6">
      <div className="text-center">
        <div className="mx-auto mb-3 inline-flex rounded-2xl bg-amber-500/10 p-4 text-amber-600 dark:text-amber-400">
          <Clock className="h-8 w-8" />
        </div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">
          תקופת הניסיון הסתיימה
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          הניסיון של <strong>{org.name}</strong> (72 שעות) הסתיים. כדי להמשיך להשתמש במערכת - יש
          לשדרג לחשבון מלא.
        </p>
      </div>

      <Card className="border-primary/30">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>שדרוג לחשבון מלא</CardTitle>
            <Badge variant="outline">מחיר קבוע חודשי</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="font-heading text-3xl font-bold">36 ₪</span>
            <span className="text-sm text-muted-foreground">לחודש</span>
            <span className="mx-1 text-muted-foreground">·</span>
            <span className="text-sm text-muted-foreground">ללא הגבלת שאלונים</span>
          </div>

          <ul className="space-y-2 text-sm">
            {INCLUDED.map((f) => (
              <li key={f} className="flex items-start gap-2">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
            השדרוג מתבצע בתיאום מול שירות הלקוחות. שלחו לנו מייל ונפעיל את החשבון.
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button asChild size="lg" className="flex-1">
              <a href={`mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`}>
                <Mail className="h-4 w-4" />
                לשדרוג - {SUPPORT_EMAIL}
              </a>
            </Button>
            <Button asChild variant="outline" size="lg">
              <a href="/auth/logout">התנתק</a>
            </Button>
          </div>

          <div className="border-t pt-4">
            <Button
              variant="outline"
              size="lg"
              className="w-full"
              onClick={downloadRawData}
              disabled={downloading}
            >
              <Download className="h-4 w-4" />
              {downloading ? 'מכין הורדה...' : 'הורדת הנתונים הגולמיים (ZIP)'}
            </Button>
            <p className="mt-2 text-center text-xs text-muted-foreground">
              גיבוי מלא של הלקוחות, השאלונים, המחזורים והרשומות בקובצי אקסל - זמין גם ללא שדרוג.
            </p>
          </div>
        </CardContent>
      </Card>

      <p className="flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
        <Phone className="h-3.5 w-3.5 text-primary" />
        Easy Call · מבית abaye
      </p>
    </div>
  );
}
